import { Check, ShieldOff, Loader2 } from 'lucide-react';
import { Button } from '../new-components/ui/button';

const ACCREDITATION_CRITERIA = [
  {
    id: 'net-worth',
    label: 'My net worth is $1M (excluding primary residence)',
  },
  {
    id: 'individual-income',
    label: 'My individual income was $200k+ for each of the past two years',
  },
  {
    id: 'joint-income',
    label: 'My joint household income was $300k+ for each of the past two years',
  },
  {
    id: 'finra',
    label: 'I and/or anyone in my household is associated with a FINRA Member',
  },
  {
    id: 'shareholder',
    label: 'I and/or anyone in my household or immediate family is a 10% shareholder, officer, or member of the board of directors of a publicly traded company',
  },
];

const NON_ACCREDITED_ID = 'non-accredited';

interface AccreditationStepProps {
  selected: Set<string>;
  onToggle: (id: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  error?: string | null;
  /** True while the account is being created after this step. */
  loading?: boolean;
}

export function AccreditationStep({ selected, onToggle, onSubmit, error, loading = false }: AccreditationStepProps) {
  const isNonAccredited = selected.has(NON_ACCREDITED_ID);

  const handleToggle = (id: string) => {
    if (loading) return;
    if (id === NON_ACCREDITED_ID) {
      // If selecting non-accredited, clear all other criteria
      if (!isNonAccredited) {
        // Clear existing by toggling them off, then mark non-accredited
        selected.forEach(existingId => {
          if (existingId !== NON_ACCREDITED_ID) onToggle(existingId);
        });
        onToggle(NON_ACCREDITED_ID);
      } else {
        onToggle(NON_ACCREDITED_ID);
      }
    } else {
      // If selecting a criteria, remove non-accredited
      if (isNonAccredited) onToggle(NON_ACCREDITED_ID);
      onToggle(id);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col h-full">
      <div className="space-y-2 shrink-0">
        <h1 className="text-3xl md:text-4xl font-medium">
          Do any of these apply to you?
        </h1>
        <p className="text-base text-muted-foreground">
          You can still invest even if none of these apply.
        </p>
      </div>

      <div className="mt-5 space-y-2">
        {ACCREDITATION_CRITERIA.map((criteria) => (
          <button
            key={criteria.id}
            type="button"
            onClick={() => handleToggle(criteria.id)}
            disabled={loading}
            className={`w-full flex items-start justify-between gap-4 px-5 py-3 rounded-xl border-2 transition-all duration-200 text-left active:scale-[0.99] motion-reduce:active:scale-100 ${
              selected.has(criteria.id)
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/30 bg-background'
            }`}
          >
            <span className="text-sm leading-snug flex-1">
              {criteria.label}
            </span>
            <div
              className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-all ${
                selected.has(criteria.id)
                  ? 'bg-primary'
                  : 'border-2 border-border bg-background'
              }`}
            >
              <Check
                className={`w-4 h-4 text-primary-foreground transition-transform duration-200 ${
                  selected.has(criteria.id) ? 'scale-100' : 'scale-0'
                }`}
                strokeWidth={3}
              />
            </div>
          </button>
        ))}

        {/* Non-accredited declaration */}
        <div className="pt-2 border-t border-border/50">
          <button
            type="button"
            onClick={() => handleToggle(NON_ACCREDITED_ID)}
            disabled={loading}
            className={`w-full flex items-start justify-between gap-4 px-5 py-3 rounded-xl border-2 transition-all duration-200 text-left active:scale-[0.99] motion-reduce:active:scale-100 disabled:cursor-not-allowed ${
              isNonAccredited
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/30 bg-background'
            }`}
          >
            <div className="flex items-start gap-3 flex-1">
              <ShieldOff className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isNonAccredited ? 'text-primary' : 'text-muted-foreground'}`} />
              <div>
                <span className="text-sm leading-snug font-medium">
                  None of these apply to me
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  I declare that I am a non-accredited investor. I understand that access to certain offerings may require admin approval.
                </p>
              </div>
            </div>
            <div
              className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-all ${
                isNonAccredited
                  ? 'bg-primary'
                  : 'border-2 border-border bg-background'
              }`}
            >
              <Check
                className={`w-4 h-4 text-primary-foreground transition-transform duration-200 ${
                  isNonAccredited ? 'scale-100' : 'scale-0'
                }`}
                strokeWidth={3}
              />
            </div>
          </button>
        </div>
      </div>

      <div className="mt-auto pt-5 shrink-0 space-y-3">
        {error && <p key={error} className="text-sm text-destructive rise-in">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full h-14 text-lg disabled:opacity-60">
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Creating your account…
            </>
          ) : (
            'Create Account'
          )}
        </Button>
      </div>
    </form>
  );
}
